import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Search, Users, Mail, Phone, Loader2, CheckCircle2, MinusCircle, XCircle, HelpCircle } from "lucide-react";
import { api, formatPrice } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { PageHeader } from "../../components/PageHeader";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";

const STATUS_ICON = {
  ok: { icon: CheckCircle2, cls: "text-success" },
  partial: { icon: MinusCircle, cls: "text-warning" },
  no: { icon: XCircle, cls: "text-danger" },
  unknown: { icon: HelpCircle, cls: "text-slate-400" },
};

const scoreColor = (s) => (s >= 80 ? "bg-success" : s >= 60 ? "bg-warning" : "bg-slate-400");

const FactorList = ({ factors }) => (
  <ul className="space-y-2">
    {factors.map((f, i) => {
      const si = STATUS_ICON[f.status] || STATUS_ICON.unknown;
      return (
        <li key={i} className="flex items-start gap-2.5 text-sm">
          <si.icon size={17} className={`mt-0.5 shrink-0 ${si.cls}`} />
          <div>
            <span className="font-medium text-navy">{f.label}</span>
            <span className="text-slate-400"> · {f.points}/{f.max_points}</span>
            {f.detail && <div className="text-slate-500">{f.detail}</div>}
          </div>
        </li>
      );
    })}
  </ul>
);

const Kandidaten = () => {
  const { lid } = useParams();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("score");
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    api(`/makelaar/listings/${lid}/candidates`).then(setData).catch(() => setError(true));
  }, [lid]);

  const openDetail = async (uid) => {
    setLoadingDetail(true);
    setDetail({});
    try { setDetail(await api(`/makelaar/listings/${lid}/candidates/${uid}`)); }
    catch { setDetail(null); } finally { setLoadingDetail(false); }
  };

  const candidates = useMemo(() => {
    let list = data?.candidates || [];
    if (q.trim()) list = list.filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase()));
    list = [...list].sort((a, b) => (sort === "score" ? b.score - a.score : (b.applied_at || "").localeCompare(a.applied_at || "")));
    return list;
  }, [data, q, sort]);

  if (error) return <div className="mx-auto max-w-md px-4 py-24 text-center text-slate-500">{t("p2.kand.notFound")}</div>;

  const listing = data?.listing;

  return (
    <div>
      <PageHeader
        icon={Users}
        title={t("p2.kand.title")}
        subtitle={listing ? `${listing.title} · ${listing.city} · ${formatPrice(listing.price)}` : ""}
      />
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <Link to="/makelaar" className="mb-5 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-navy">
          <ArrowLeft size={16} /> {t("p2.kand.back")}
        </Link>

        <div className="mb-5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="rounded-xl pl-9" placeholder={t("p2.kand.search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="kandidaat-search" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[170px] rounded-xl" data-testid="kandidaat-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="score">{t("p2.kand.sortScore")}</SelectItem>
              <SelectItem value="recent">{t("p2.kand.sortRecent")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <p className="mb-3 text-xs text-slate-400">{t("p2.kand.disclaimer")}</p>

        {data === null ? (
          <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-slate-100" />)}</div>
        ) : candidates.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border p-16 text-center" data-testid="kandidaten-empty">
            <Users className="mx-auto text-slate-300" size={44} />
            <p className="mt-4 text-slate-500">{t("p2.kand.empty")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {candidates.map((c) => (
              <button
                key={c.user_id}
                onClick={() => openDetail(c.user_id)}
                data-testid={`kandidaat-${c.user_id}`}
                className="flex w-full items-center gap-4 rounded-2xl border border-border bg-white p-4 text-left shadow-sm transition-colors hover:border-brandblue"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-2xl text-sm font-extrabold text-white ${scoreColor(c.score)}`}>{c.score}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-navy">{c.name}</div>
                  <div className="text-sm text-slate-500">{(c.factors || []).slice(0, 2).map((f) => f.label).join(" · ")}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={detail !== null} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{t("p2.kand.candidate")}</DialogTitle></DialogHeader>
          {loadingDetail ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brandblue" size={28} /></div>
          ) : detail ? (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <span className={`flex h-12 w-12 items-center justify-center rounded-2xl text-sm font-extrabold text-white ${scoreColor(detail.score || 0)}`}>{detail.score}</span>
                <div>
                  <div className="font-display text-lg font-bold text-navy">{detail.name}</div>
                  <div className="flex flex-col gap-0.5 text-sm text-slate-500">
                    {detail.email && <span className="flex items-center gap-1.5"><Mail size={13} /> {detail.email}</span>}
                    {detail.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {detail.phone}</span>}
                  </div>
                </div>
              </div>
              {detail.reply && (
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="font-display text-sm font-bold text-navy">{detail.reply.subject}</div>
                  <p className="mt-1.5 whitespace-pre-line text-sm text-slate-600">{detail.reply.body}</p>
                </div>
              )}
              {detail.factors?.length > 0 && (
                <div>
                  <h3 className="mb-2 font-display text-sm font-bold text-navy">{t("p2.kand.assessment")}</h3>
                  <FactorList factors={detail.factors} />
                </div>
              )}
            </div>
          ) : (
            <p className="py-8 text-center text-slate-500">{t("p2.kand.loadFail")}</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Kandidaten;
